import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Chip, Button, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { fetchData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const TERMS = ["Term 1", "Term 2", "Term 3"];

const statusConfig = {
  Excellent: { color: "#4caf50" },
  Good:      { color: "#2196f3" },
  Average:   { color: "#ff9800" },
  Poor:      { color: "#f44336" },
};

/* ── Fallback mock data ── */
const fallbackStudent = {
  name:         "Andrei Bloom",
  grade:        "Nilai 3",
  section:      "A",
  gender:       "Male",
  period:       "Jan - Mar 2025",
  teacher:      "Kavitha ravi",
  overall:      70,
  overallGrade: "B",
  attendance:   94,
  photo:        null,
};

const fallbackSubjects = [
  { subject: "Tamil Reading", max: 20, marks: 18, classAvg: "A", percentage: 20, bar: 18, grade: "2",  status: "Good" },
  { subject: "Elana",         max: 20, marks: 20, classAvg: "C", percentage: 20, bar: 20, grade: "20", status: "Excellent" },
  { subject: "Tamil Writing", max: 20, marks: 20, classAvg: "B", percentage: 20, bar: 20, grade: "0",  status: "Excellent" },
  { subject: "Grammar",       max: 20, marks: 20, classAvg: "A", percentage: 20, bar: 20, grade: "0",  status: "Excellent" },
  { subject: "Speaking",      max: 20, marks: 10, classAvg: "B", percentage: 20, bar: 10, grade: "10", status: "Poor" },
  { subject: "Home Work",     max: 20, marks: 20, classAvg: "C", percentage: 20, bar: 20, grade: "0",  status: "Excellent" },
  { subject: "Total",         max: 20, marks: 20, classAvg: "C", percentage: 20, bar: 20, grade: "0",  status: "Rank", rank: "#31/14" },
];

/* Status dot component */
const StatusCell = ({ status, rank }) => {
  if (status === "Rank") {
    return (
      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#f59e0b" }}>
        Rank {rank}
      </Typography>
    );
  }
  const cfg = statusConfig[status] || { color: "#8F95B2" };
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: cfg.color, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "13px", color: cfg.color, fontWeight: 500 }}>{status}</Typography>
    </Box>
  );
};

const ProgressReport = () => {
  const [termTab,     setTermTab]     = useState(1); // Term 2 active by default
  const [reportData,  setReportData]  = useState(null);
  const [toast,       setToast]       = useState({ open: false, severity: "success", message: "" });

  const applicationId = sessionStorage.getItem("applicationId") || "";
  const academicYear  = "2024-25";

  const loadReport = useCallback(async () => {
    if (!applicationId) return;
    try {
      const term     = TERMS[termTab].replace(" ", "_").toLowerCase();
      const response = await fetchData(
        `student/progress-report/${applicationId}?academicYear=${academicYear}&term=${term}`
      );
      if (response?.data) setReportData(response.data);
    } catch (_) {
      // Silently fall through — inline fallback handles display
    }
  }, [applicationId, termTab, academicYear]);

  useEffect(() => { loadReport(); }, [loadReport]);

  const handleExport = async () => {
    setToast({ open: true, severity: "info", message: "Preparing export…" });
    try {
      const term = TERMS[termTab].replace(" ", "_").toLowerCase();
      const res  = await fetchData(
        `student/progress-report/${applicationId}/pdf?academicYear=${academicYear}&term=${term}`
      );
      if (res?.downloadUrl) {
        const a = document.createElement("a");
        a.href = res.downloadUrl;
        a.download = `progress-report-${term}.pdf`;
        a.click();
        setToast({ open: true, severity: "success", message: "PDF downloaded" });
      } else {
        window.print();
        setToast({ open: true, severity: "info", message: "Print dialog opened — save as PDF" });
      }
    } catch (_) {
      window.print();
      setToast({ open: true, severity: "info", message: "Print dialog opened — save as PDF" });
    }
  };

  /* Resolved data (API or fallback) */
  const student  = reportData?.student  || fallbackStudent;
  const subjects = reportData?.subjects || fallbackSubjects;

  const initials = student.name
    ? student.name.split(" ").map((n) => n[0]).join("").toUpperCase()
    : "AB";

  return (
    <div style={{ textAlign: "left", padding: "1rem" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />

      <p style={{ color: "rgba(0,0,0,0.45)", fontWeight: "bold", fontSize: "14px", margin: "0 0 12px" }}>
        Pages / Progress Report
      </p>

      {/* ── Main card ── */}
      <Paper elevation={0} sx={{ borderRadius: "16px", boxShadow: "0 2px 12px rgba(0,0,0,0.07)", overflow: "hidden" }}>

        {/* ── Card header ── */}
        <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #F3F4F6" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            {/* Left */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontWeight: "bold", fontSize: "18px", color: "#111827" }}>
                  My Grades &amp; Progress
                </Typography>
                <Chip
                  label={`Section ${student.section || "A"}`}
                  size="small"
                  sx={{ backgroundColor: "#e8f5e9", color: "#4caf50", fontWeight: 600, fontSize: "12px" }}
                />
              </Box>
              <Typography sx={{ fontSize: "13px", color: "#6B7280", mt: 0.5 }}>
                Academic Year {academicYear}&nbsp;&nbsp;
                <strong style={{ color: "#374151" }}>{student.grade || "Nilai 3"}</strong>
              </Typography>
            </Box>

            {/* Export button */}
            <Button
              startIcon={<DownloadIcon sx={{ fontSize: "16px" }} />}
              onClick={handleExport}
              sx={{
                border: "1px solid #D5D7DA", borderRadius: "8px", color: "#374151",
                textTransform: "none", fontSize: "13px", px: 2, py: 0.8,
                "&:hover": { backgroundColor: "#f9fafb" },
              }}
            >
              Export
            </Button>
          </Box>

          {/* ── Term tabs ── */}
          <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
            {TERMS.map((term, idx) => (
              <Button
                key={term}
                onClick={() => setTermTab(idx)}
                sx={{
                  textTransform: "none", fontSize: "13px", fontWeight: 500,
                  px: 2.5, py: 0.6, borderRadius: "20px",
                  backgroundColor: termTab === idx ? "#4caf50" : "transparent",
                  color: termTab === idx ? "#fff" : "#6B7280",
                  "&:hover": {
                    backgroundColor: termTab === idx ? "#43a047" : "#f3f4f6",
                  },
                  minWidth: "unset",
                }}
              >
                {term}
              </Button>
            ))}
          </Box>
        </Box>

        {/* ── Student profile banner ── */}
        <Box
          sx={{
            mx: 3, mt: 2.5, mb: 2.5, borderRadius: "14px", overflow: "hidden",
            background: "linear-gradient(135deg, #1a237e 0%, #283593 45%, #4527a0 100%)",
            px: 3, py: 2.5,
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2,
          }}
        >
          {/* Photo + name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={student.photo || undefined}
              sx={{
                width: 70, height: 70, border: "3px solid rgba(255,255,255,0.4)",
                fontSize: "22px", fontWeight: "bold", backgroundColor: "#5c6bc0",
              }}
            >
              {initials}
            </Avatar>
            <Box>
              <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "20px", lineHeight: 1.3 }}>
                {student.name || "Student Name"}
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.72)", fontSize: "12.5px", mt: 0.4 }}>
                {student.grade || "Nilai 3"} &nbsp;·&nbsp; Sec {student.section || "A"} &nbsp;·&nbsp;
                {student.gender || "—"}&nbsp;&nbsp;
                {student.period || "Jan - Mar 2025"}&nbsp;&nbsp;
                Teacher : {student.teacher || "—"}
              </Typography>
            </Box>
          </Box>

          {/* Stat boxes */}
          <Box sx={{ display: "flex", gap: 1.5 }}>
            {[
              { value: `${student.overall || 70}%`, label: "Overall" },
              { value: student.overallGrade || "B",  label: "Grade" },
              { value: `${student.attendance || 94}%`, label: "Attendance" },
            ].map((stat) => (
              <Box
                key={stat.label}
                sx={{
                  backgroundColor: "rgba(0,0,0,0.30)",
                  borderRadius: "10px", px: 2.5, py: 1.2,
                  textAlign: "center", minWidth: 80,
                }}
              >
                <Typography sx={{ color: "#fff", fontWeight: "bold", fontSize: "20px", lineHeight: 1.2 }}>
                  {stat.value}
                </Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "11px", mt: 0.2 }}>
                  {stat.label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ── Grades table ── */}
        <TableContainer sx={{ px: 1, pb: 0 }}>
          <Table>
            <TableHead>
              <TableRow>
                {["Subject", "Max", "Marks", "Class AVG", "% Score", "Bar", "Grade", "Status"].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 600, fontSize: "13px", color: "#374151",
                      borderBottom: "1px solid #E5E7EB", py: 1.5, whiteSpace: "nowrap",
                    }}
                  >
                    {h}&nbsp;<span style={{ opacity: 0.45, fontSize: "11px" }}>↓</span>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {subjects.map((row, idx) => (
                <TableRow
                  key={idx}
                  sx={{
                    "&:hover": { backgroundColor: "#f9fafb" },
                    "&:last-child td": { borderBottom: 0 },
                    backgroundColor: row.subject === "Total" ? "#fafafa" : "transparent",
                  }}
                >
                  <TableCell sx={{ fontSize: "13px", fontWeight: row.subject === "Total" ? 700 : 500, color: "#111827", py: 1.4 }}>
                    {row.subject}
                  </TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#6B7280", py: 1.4 }}>{row.max}</TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151", py: 1.4 }}>{row.marks}</TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151", py: 1.4 }}>{row.classAvg}</TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151", py: 1.4 }}>{row.percentage}</TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151", py: 1.4 }}>{row.bar}</TableCell>
                  <TableCell sx={{ fontSize: "13px", color: "#374151", py: 1.4 }}>{row.grade}</TableCell>
                  <TableCell sx={{ py: 1.4 }}>
                    <StatusCell status={row.status} rank={row.rank} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Teacher Comments ── */}
        <Box sx={{ px: 3, pt: 2, pb: 3 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#374151", mb: 1 }}>
            Teacher's Comments:
          </Typography>
          <Box
            sx={{
              borderRadius: "10px",
              border: "1px solid #E5E7EB",
              backgroundColor: "#F9FAFB",
              px: 2.5,
              py: 2,
              minHeight: "72px",
            }}
          >
            <Typography sx={{ fontSize: "13px", color: reportData?.teacherComments ? "#111827" : "#9CA3AF", fontStyle: reportData?.teacherComments ? "normal" : "italic" }}>
              {reportData?.teacherComments || "No comments added yet."}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </div>
  );
};

export default ProgressReport;
